/**
 * useVehicleTypes - Hook to fetch vehicle types from tariffs API
 * Single source of truth for vehicle types across the application
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

// Default vehicle types (fallback if API fails)
const DEFAULT_VEHICLE_TYPES = [
  { vehicle_id: 'berline', name: 'Berline Classique', max_passengers: 3, max_luggage: 3, is_active: true },
  { vehicle_id: 'berline_premium', name: 'Berline Premium', max_passengers: 3, max_luggage: 3, is_active: true },
  { vehicle_id: 'suv', name: 'SUV', max_passengers: 5, max_luggage: 5, is_active: true },
  { vehicle_id: 'van', name: 'Van', max_passengers: 7, max_luggage: 7, is_active: true },
];

// Color mapping for vehicle types in UI - matching the screenshot colors
export const VEHICLE_TYPE_COLORS = {
  'berline': { color: 'text-blue-400', bg: 'bg-blue-500/20', iconColor: '#60A5FA' },
  'berline_premium': { color: 'text-amber-400', bg: 'bg-amber-500/20', iconColor: '#FBBF24' },
  'suv': { color: 'text-green-400', bg: 'bg-green-500/20', iconColor: '#4ADE80' },
  'van': { color: 'text-purple-400', bg: 'bg-purple-500/20', iconColor: '#C084FC' },
  'minibus': { color: 'text-orange-400', bg: 'bg-orange-500/20', iconColor: '#FB923C' },
};

// SVG Car Icon Component - Used for consistent vehicle display
export const VehicleIcon = ({ vehicleType, className = "w-4 h-4", size = 16 }) => {
  const colors = VEHICLE_TYPE_COLORS[vehicleType] || VEHICLE_TYPE_COLORS['berline'];
  const iconColor = colors.iconColor || '#9CA3AF';
  
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke={iconColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      width={size}
      height={size}
    >
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </svg>
  );
};

export const useVehicleTypes = (includeInactive = false) => {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchVehicleTypes = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/tariffs/vehicle-types`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { include_inactive: includeInactive }
      });
      
      const types = response.data.vehicle_types || response.data || [];
      setVehicleTypes(types.length > 0 ? types : DEFAULT_VEHICLE_TYPES);
      setError(null);
    } catch (err) {
      console.error('Error fetching vehicle types:', err);
      setError(err.message);
      setVehicleTypes(DEFAULT_VEHICLE_TYPES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleTypes();
  }, [includeInactive]);

  // Helper to get vehicle type by ID
  const getVehicleType = (vehicleId) => {
    return vehicleTypes.find(v => v.vehicle_id === vehicleId) || null;
  };

  // Helper to get display config for a vehicle type
  const getVehicleTypeConfig = (vehicleId) => {
    const type = getVehicleType(vehicleId);
    const colors = VEHICLE_TYPE_COLORS[vehicleId] || { color: 'text-gray-400', bg: 'bg-gray-500/20' };
    
    return {
      id: vehicleId,
      label: type?.name || vehicleId,
      maxPassengers: type?.max_passengers || 4,
      maxLuggage: type?.max_luggage || 4,
      ...colors
    };
  };

  // Get active vehicle types only
  const activeVehicleTypes = vehicleTypes.filter(v => v.is_active !== false);

  return {
    vehicleTypes,
    activeVehicleTypes,
    loading,
    error,
    refetch: fetchVehicleTypes,
    getVehicleType,
    getVehicleTypeConfig
  };
};

export default useVehicleTypes;
